// Telemetry groups UUID-shaped IDs regardless of their version.
const UUID_SEGMENT =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const NUMERIC_SEGMENT = /^\d+$/
const DEDUPE_WINDOW_MS = 30 * 60 * 1000

interface ApiErrorSignatureInput {
    status: number
    code: string
    method: string
    path: string
}

interface SignatureWindow {
    startedAt: number
    repeatCount: number
}

export interface ApiErrorCaptureDecision {
    capture: boolean
    pathTemplate: string
    repeatCount?: number
}

const signatureWindows = new Map<string, SignatureWindow>()

function createSignature(input: ApiErrorSignatureInput): string {
    return [
        input.status,
        input.code,
        input.method.toUpperCase(),
        normalizeApiPath(input.path),
    ].join('\u0000')
}

export function normalizeApiPath(path: string): string {
    const [pathname] = path.split(/[?#]/, 1)
    return pathname
        .split('/')
        .map((segment) =>
            UUID_SEGMENT.test(segment) || NUMERIC_SEGMENT.test(segment)
                ? ':id'
                : segment,
        )
        .join('/')
}

export function decideApiErrorCapture(
    input: ApiErrorSignatureInput,
    now = Date.now(),
): ApiErrorCaptureDecision {
    const pathTemplate = normalizeApiPath(input.path)
    const signature = createSignature(input)
    const current = signatureWindows.get(signature)

    if (!current) {
        return { capture: true, pathTemplate }
    }

    if (now - current.startedAt < DEDUPE_WINDOW_MS) {
        current.repeatCount += 1
        return { capture: false, pathTemplate }
    }

    const repeatCount = current.repeatCount + 1
    return { capture: true, pathTemplate, repeatCount }
}

export function markApiErrorCaptured(
    input: ApiErrorSignatureInput,
    now = Date.now(),
): void {
    signatureWindows.set(createSignature(input), {
        startedAt: now,
        repeatCount: 0,
    })
}

export function markApiErrorCaptureFailed(input: ApiErrorSignatureInput): void {
    const current = signatureWindows.get(createSignature(input))
    if (current) current.repeatCount += 1
}

export function clearApiErrorSignatures(): void {
    signatureWindows.clear()
}
